import type { CompRef } from '@wix/document-services-types';

import type { EditorReactComponent } from '@wix/component-protocol/schema';
import { DependencyToken, IDependencyAPI } from '../dependencies';
import {
  EditorReactComponentExtensionType,
  IManifestStoreEntity,
  ManifestComponentExtensionType,
} from '../stores';
import type { EditorPointer } from '@wix/editor-document-logic-api';

export enum ManifestBuilderStrategy {
  Merge = 'merge',
  Intersection = 'intersection',
}

/**
 * TODO:
 * should we exclude 'states' | 'presets'?
 */
export const REDUCIBLE_KEYS = [
  'cssProperties',
  'cssCustomProperties',
  'data',
  'customActions',
  'states',
  'presets',
  'actions',
] as const;

export type ReducibleKey = (typeof REDUCIBLE_KEYS)[number];

export interface IVirtualManifestItemMapping {
  runtimeKey: string;
  originalKey: string;
  editorPointer: EditorPointer<CompRef>;
  extensionType: ManifestComponentExtensionType;
  // for value's setter
  encodeValue?: (currentValue: unknown, partialValue: unknown) => string;
  // for value's getter
  decodeValue?: (currentValue: unknown) => string;
  // type?: 'cssProperty' | 'cssCustomProperties';
}

export enum VirtualManifestElementType {
  Root = 'root',
  Inline = 'inline',
  RefElement = 'refElement',
}

export type IVirtualManifestMappings = Partial<
  Record<ReducibleKey, Record<string, IVirtualManifestItemMapping[]>>
>;

// TODO:
export interface IVirtualManifestResolved extends IVirtualManifest {
  type: 'resolved';
}

export interface IVirtualManifest {
  virtualManifestId: string;
  childPath: number[];
  strategy?: ManifestBuilderStrategy;
  metadata?: {
    displayName?: string;
  } & Record<string, any>;

  overriddenDependencies?: Partial<{
    [K in DependencyToken]: Extract<IDependencyAPI, { type: K }>['api'];
  }>;

  innerElementsIndexes: {
    rootEditorPointer: EditorPointer<CompRef>;
    elements: Record<string, number[]>;
  }[];
  /**
   * contains all original manifests used to build virtual context
   * along with editorPointer and metadata
   */
  manifests: {
    isAutoInjected?: boolean;
    store: IManifestStoreEntity<EditorReactComponentExtensionType>;
    editorPointer: EditorPointer<CompRef>;
  }[];
  /**
   * built (merged) manifests with children
   */
  tree: {
    elementType: VirtualManifestElementType;
    breadcrumbs: string[];
    elementName: string | null;
    editorPointer: EditorPointer<CompRef> | null;
    extensionType: ManifestComponentExtensionType | null;
    componentType: string;
    /**
     * TODO: we need correctly separate root editorElement vs child InlineElement
     * since their types are a bit different
     */
    manifest: EditorReactComponent;
    mappings: IVirtualManifestMappings;
    /**
     * Carries the wrapper `ElementItem.deprecated` flag for child elements.
     * The reduced `manifest.editorElement` does not retain the elements map,
     * so this is the surviving source for element-level deprecation.
     */
    deprecated?: boolean;
    children: IVirtualManifest['tree'][];
  };
}

export interface IVirtualBuilder {
  build(manifestId: string): IVirtualManifest;
}
